import firebase from '../firebase-config';
import { 
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged
} from 'firebase/auth';
import { firestoreService } from './firestoreService';
import axios from 'axios';
import { API_URL as DEPLOYED_API_URL } from '../config/apiConfig';

// Use deployed server URL
const API_URL = DEPLOYED_API_URL;

const { auth, providers, browserPopupRedirectResolver } = firebase;

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';

class AuthService {
  constructor() {
    // Set up auth state listener
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idToken = await user.getIdToken();
          axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
          
          const subscription = await this.getSubscriptionStatus(user.uid);
          if (subscription) {
            localStorage.setItem('userSubscription', JSON.stringify(subscription));
          }
        } catch (error) {
          console.error('Error checking subscription on auth state change:', error);
        }
      } else {
        delete axios.defaults.headers.common['Authorization'];
        localStorage.removeItem('userSubscription');
      }
    });
  }

  async login(email) {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email });
      if (response.data.verificationCode) {
        localStorage.setItem('tempEmail', email);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async verifyCode(code) {
    try {
      const email = localStorage.getItem('tempEmail');
      const response = await axios.post(`${API_URL}/auth/verify`, { 
        email, 
        code 
      });
      
      if (response.data.token) {
        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('userEmail', email);
        localStorage.removeItem('tempEmail');
        
        // Get user subscription status
        const user = auth.currentUser;
        if (user) {
          const subscription = await this.getSubscriptionStatus(user.uid);
          if (subscription) {
            localStorage.setItem('userSubscription', JSON.stringify(subscription));
          }
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Verification error:', error);
      throw error;
    }
  }

  async getSubscriptionStatus(userId) {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        console.error('No authentication token available');
        return null;
      }

      // The error shows that this endpoint shouldn't have /api prefix
      const subscriptionEndpoint = `${API_URL}/subscription/status`;
      console.log('Checking subscription status at:', subscriptionEndpoint);
      
      try {
        const response = await axios.get(subscriptionEndpoint, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.data) {
          // Check if subscription is still valid
          const expiryDate = new Date(response.data.expiryDate);
          if (expiryDate > new Date() && response.data.isActive) {
            return response.data;
          }
        }
        return null;
      } catch (axiosError) {
        console.error('Subscription status error:', axiosError);
        
        // For development/testing, create a mock subscription
        if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
          console.log('Using mock subscription data for development');
          const mockSubscription = {
            isActive: true,
            type: 'monthly',
            startDate: new Date().toISOString(),
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            paymentId: 'mock_payment_' + Date.now(),
            amount: 99.99,
            currency: 'ZAR'
          };
          return mockSubscription;
        }
        return null;
      }
    } catch (error) {
      console.error('Subscription status error:', error);
      return null;
    }
  }

  async loginWithProvider(providerName, useRedirect = false) {
    try {
      const provider = providers[providerName.toLowerCase()];
      if (!provider) {
        throw new Error(`Provider ${providerName} not supported`);
      }

      // Clear any existing auth state and storage
      await this.clearStorage();

      if (useRedirect) {
        // Use redirect method
        await signInWithRedirect(auth, provider);
        return { success: true, redirect: true };
      } else {
        // Try popup first
        try {
          const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
          if (result?.user) {
            await this.handleSuccessfulLogin(result.user);
            return { success: true };
          }
          throw new Error('No user returned from sign in');
        } catch (popupError) {
          console.log('Popup auth error:', popupError);
          
          // If popup was blocked, closed, or quota exceeded, try redirect
          if (popupError.code === 'auth/popup-closed-by-user' || 
              popupError.code === 'auth/popup-blocked' ||
              popupError.message?.includes('quota')) {
            console.log('Popup failed, trying redirect');
            await signInWithRedirect(auth, provider);
            return { success: true, redirect: true };
          }
          
          // If it's a different error, throw it
          throw popupError;
        }
      }
    } catch (error) {
      console.error('Social login error:', error);
      throw error;
    }
  }

  async clearStorage() {
    try {
      // Clear all localStorage items
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear any existing auth state
      await signOut(auth);
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  async handleSuccessfulLogin(user) {
    try {
      // Store user data in sessionStorage
      const token = await user.getIdToken();
      sessionStorage.setItem('userToken', token);
      sessionStorage.setItem('userEmail', user.email);
      sessionStorage.setItem('userId', user.uid);
      
      // Get user subscription status
      const subscription = await this.getSubscriptionStatus(user.uid);
      if (subscription) {
        sessionStorage.setItem('userSubscription', JSON.stringify(subscription));
      }
    } catch (error) {
      console.error('Error handling successful login:', error);
      throw error;
    }
  }

  async handleRedirectResult() {
    try {
      const result = await getRedirectResult(auth);
      if (result?.user) {
        await this.handleSuccessfulLogin(result.user);
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      console.error('Error handling redirect result:', error);
      return { success: false, error };
    }
  }

  isAuthenticated() {
    const user = auth.currentUser;
    const isAuth = !!user;
    console.log('Checking auth - Current user:', user?.email, 'Is authenticated:', isAuth);
    return isAuth;
  }

  async logout() {
    try {
      await signOut(auth);
      // Clear both localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  hasActiveSubscription() {
    // Check sessionStorage first, then localStorage
    const subscription = sessionStorage.getItem('userSubscription') || 
                        localStorage.getItem('userSubscription');
    if (!subscription) return false;
    
    try {
      const subData = JSON.parse(subscription);
      const expiryDate = new Date(subData.expiryDate);
      return expiryDate > new Date() && subData.isActive;
    } catch (error) {
      console.error('Error parsing subscription:', error);
      return false;
    }
  }

  async signUpWithEmailAndPassword(email, password) {
    try {
      console.log('Attempting sign up with email/password:', email);
      
      // Ensure email and password are not empty
      if (!email || !password) {
        throw new Error('Email and password are required');
      }
      
      // Clear any previous auth state
      await signOut(auth);
      
      // Attempt sign up with Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      if (!user) {
        throw new Error('Account creation failed - no user returned');
      }
      
      console.log('Sign up successful for:', user.email);
      
      // Send email verification
      await sendEmailVerification(user);
      
      // Get the Firebase ID token
      const token = await user.getIdToken();
      
      // Store user info in localStorage and sessionStorage for redundancy
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        createdAt: new Date().toISOString()
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.setItem('userToken', token);
      sessionStorage.setItem('userEmail', user.email);
      sessionStorage.setItem('userId', user.uid);
      
      // Set axios authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true, user };
    } catch (error) {
      console.error('Sign up error:', error.code, error.message);
      // Rethrow with Firebase error code preserved
      throw error;
    }
  }

  async signInWithEmailAndPassword(email, password) {
    try {
      console.log('Attempting sign in with email/password:', email);
      
      // Ensure email and password are not empty
      if (!email || !password) {
        throw new Error('Email and password are required');
      }
      
      // Clear any previous auth state
      await signOut(auth);
      
      try {
        // First attempt: Try standard email/password sign in
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        if (!user) {
          throw new Error('Authentication failed - no user returned');
        }
        
        console.log('Sign in successful for:', user.email);
        
        // Standard sign-in success - process and return
        return this.processSuccessfulSignIn(user);
      } catch (emailSignInError) {
        console.log('Standard email sign-in failed, error:', emailSignInError.code);
        
        // If the error is "auth/invalid-login-credentials" or "auth/user-not-found", try Google fallback
        if (emailSignInError.code === 'auth/invalid-login-credentials' || 
            emailSignInError.code === 'auth/user-not-found') {
          
          // Check if email ends with gmail.com or googlemail.com
          if (email.endsWith('@gmail.com') || email.endsWith('@googlemail.com')) {
            console.log('Email is Gmail, attempting Google authentication fallback');
            
            try {
              // Try Google authentication as fallback
              const provider = providers.google;
              
              // Set the login hint to the email address to pre-fill the Google sign-in
              provider.setCustomParameters({
                login_hint: email
              });
              
              // Use popup for better UX
              const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
              
              if (result?.user && result.user.email.toLowerCase() === email.toLowerCase()) {
                console.log('Google fallback authentication successful for:', result.user.email);
                return this.processSuccessfulSignIn(result.user);
              } else {
                // User authenticated with Google but with a different email
                await signOut(auth); // Sign out the Google user
                throw new Error('The Google account you signed in with uses a different email address.');
              }
            } catch (googleAuthError) {
              console.error('Google fallback authentication failed:', googleAuthError);
              // Rethrow the original email/password error instead of the Google error
              throw emailSignInError;
            }
          } else {
            // Not a Gmail address, rethrow the original error
            throw emailSignInError;
          }
        } else {
          // For other types of errors, rethrow
          throw emailSignInError;
        }
      }
    } catch (error) {
      console.error('Sign in error:', error.code, error.message);
      // Rethrow with Firebase error code preserved
      throw error;
    }
  }
  
  // Helper function to process a successful sign-in
  async processSuccessfulSignIn(user) {
    // Get the Firebase ID token
    const token = await user.getIdToken();
    
    // Store user info in localStorage and sessionStorage for redundancy
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      photoURL: user.photoURL || '',
      lastLogin: new Date().toISOString()
    };
    
    localStorage.setItem('user', JSON.stringify(userData));
    sessionStorage.setItem('userToken', token);
    sessionStorage.setItem('userEmail', user.email);
    sessionStorage.setItem('userId', user.uid);
    
    // Set axios authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    return { success: true, user };
  }

  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }
}

export const authService = new AuthService(); 